import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Book,
  Heart,
  Wand2,
  Volume2,
  Download,
  ShoppingBag,
  Star,
  Check,
  ArrowRight,
  Play,
  Users,
  Sparkles,
  Clock,
  Shield,
  Gift,
  Smartphone,
  Award,
  ChevronDown,
  ChevronUp,
  Crown,
  Globe,
  ListMusic,
  Mic2,
  Baby,
  Users2,
  UserPlus,
  Camera,
  BookOpen,
} from "lucide-react";
import { TeddyBear } from "@/components/icons/TeddyBear";
import { motion, AnimatePresence } from "framer-motion";
import LandingNavigation from "@/components/LandingNavigation";
import { useAuth } from "@/hooks/useAuth";
import Footer from "@/components/ui/footer";

const features = [
  {
    icon: Wand2,
    title: "AI-Powered Story Generation",
    description:
      "Create unique, personalized bedtime stories in seconds using advanced AI technology",
    color: "from-purple-500 to-pink-500",
  },
  {
    icon: Baby,
    title: "Multi-Child Profiles",
    description:
      "Create profiles for each child - save their details and upload photos to auto-generate cartoon avatar characters for personalized stories",
    color: "from-rose-500 to-pink-500",
  },
  {
    icon: Users,
    title: "Character Studio (Premium Add-on)",
    description:
      "Transform photos into magical cartoon characters for your story illustrations - available as a premium subscription add-on",
    color: "from-rose-500 to-pink-500",
  },
  {
    icon: Volume2,
    title: "Professional Narration",
    description:
      "High-quality text-to-speech with natural voices that bring stories to life",
    color: "from-blue-500 to-cyan-500",
  },
  {
    icon: Mic2,
    title: "Multi-Voice Character Narration",
    description:
      "Different voices for each character - narrator, heroes, and villains get unique voices",
    color: "from-indigo-500 to-purple-500",
  },
  {
    icon: Globe,
    title: "10 Languages Supported",
    description:
      "Create and enjoy stories in English, Spanish, French, German, Chinese, Japanese, Portuguese, Italian, Russian, and Arabic",
    color: "from-emerald-500 to-teal-500",
  },
  {
    icon: ListMusic,
    title: "Story Playlists",
    description:
      "Create custom playlists with auto-play mode for uninterrupted bedtime routines",
    color: "from-violet-500 to-fuchsia-500",
  },
  {
    icon: Sparkles,
    title: "Personalized Illustrations",
    description:
      "AI-generated artwork featuring your custom characters in beautiful storybook scenes",
    color: "from-green-500 to-emerald-500",
  },
  {
    icon: Download,
    title: "PDF Downloads",
    description:
      "Save stories as PDFs and coloring books for offline reading and activities",
    color: "from-orange-500 to-red-500",
  },
  {
    icon: ShoppingBag,
    title: "Physical Books",
    description:
      "Order professionally printed story books to create lasting memories",
    color: "from-amber-500 to-yellow-500",
  },
  {
    icon: Heart,
    title: "Personalized Content",
    description:
      "Stories tailored to your child's age, interests, and learning goals",
    color: "from-pink-500 to-rose-500",
  },
  {
    icon: Smartphone,
    title: "Mobile App Support",
    description:
      "Access stories on your phone with our mobile-optimized experience and Add to Home Screen functionality",
    color: "from-indigo-500 to-purple-500",
  },
  {
    icon: Gift,
    title: "Gift Subscriptions",
    description:
      "Send the gift of storytelling with our anonymous gift subscription system",
    color: "from-teal-500 to-green-500",
  },
];

const testimonials = [
  {
    name: "Sarah M.",
    role: "Mother of 2",
    content:
      "My kids absolutely love their personalized stories! The AI creates such engaging tales that keep them entertained for hours.",
    rating: 5,
  },
  {
    name: "David L.",
    role: "Father",
    content:
      "The narration quality is amazing. It's like having a professional storyteller in our home every night.",
    rating: 5,
  },
  {
    name: "Emily R.",
    role: "Teacher & Mom",
    content:
      "The educational value is incredible. My daughter has learned so much through these interactive stories.",
    rating: 5,
  },
];

const faqs = [
  {
    question: "How does the AI story generation work?",
    answer:
      "Our AI analyzes your child's age, interests, and preferences to create unique, age-appropriate stories. Each story is completely original and tailored specifically for your child.",
  },
  {
    question: "Can I customize the stories?",
    answer:
      "Yes! You can specify themes, characters, moral lessons, and even include your child's name and favorite activities in the stories. For ultimate personalization, add our Character Studio subscription to create custom cartoon characters from family photos.",
  },
  {
    question: "What age range is this suitable for?",
    answer:
      "Our stories are designed for children ages 2-12, with content automatically adjusted based on your child's age and developmental stage.",
  },
  {
    question: "Can I download the stories?",
    answer:
      "Absolutely! You can download stories as PDFs, print them as coloring books, or even order professionally printed physical books.",
  },
  {
    question: "Is there a free trial?",
    answer:
      "Yes! We offer a 7-day free trial so you can experience all our features before committing to a subscription.",
  },
  {
    question: "Can I cancel anytime?",
    answer:
      "Yes, you can cancel your subscription at any time. There are no long-term commitments or cancellation fees.",
  },
  {
    question: "How much do additional child profiles cost?",
    answer:
      "$1.99 one-time fee per additional profile slot. Your first child profile is FREE with your subscription. Additional profiles include all features: photo upload, cartoon avatar generation, and story personalization.",
  },
];

const pricingPlans = [
  {
    name: "Monthly",
    price: "$6.99",
    period: "per month",
    description: "Perfect for trying out our service",
    features: [
      "Unlimited story generation",
      "AI-powered narration",
      "Multi-voice character narration",
      "10 languages supported",
      "Story playlists with auto-play",
      "Beautiful illustrations",
      "PDF downloads",
      "Character stickers",
      "Physical book ordering",
    ],
    cta: "Start Free Trial",
    popular: false,
  },
  {
    name: "Yearly",
    price: "$69.99",
    period: "per year",
    description: "Best value - save $13.89!",
    features: [
      "Everything in Monthly",
      "Multi-voice character narration",
      "10 languages supported",
      "Story playlists with auto-play",
      "Save $13.89 per year",
      "Priority support",
      "Exclusive seasonal stories",
      "Advanced customization options",
    ],
    cta: "Start Free Trial",
    popular: true,
  },
];

export default function LandingPage() {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [, navigate] = useLocation();
  const { user, isLoading } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!isLoading && user) {
      navigate("/home");
    }
  }, [user, isLoading, navigate]);

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <>
      <LandingNavigation />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 pt-16">
        {/* Hero Section */}
        <section className="py-12 sm:py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center lg:text-left"
              >
                <Badge className="mb-4 bg-gradient-to-r from-purple-600 to-teal-600 text-white text-sm">
                  ✨ AI-Powered Storytelling
                </Badge>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent leading-tight">
                  Magical Bedtime Stories for Your Little One
                </h1>
                <p className="text-lg sm:text-xl text-gray-700 mb-6 sm:mb-8 leading-relaxed">
                  Create personalized, AI-generated bedtime stories featuring
                  your own custom characters! Transform family photos into
                  magical cartoon characters, then watch them come to life in
                  beautifully illustrated stories. With professional narration
                  and mobile accessibility, every story is unique and tailored
                  just for your child.
                </p>

                {/* Platform Availability */}
                <div className="mb-6 sm:mb-8">
                  <div className="flex items-center justify-center lg:justify-start gap-6 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">Web Browser</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="font-medium">iOS App</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span className="font-medium">Android App</span>
                    </div>
                  </div>
                  <p className="text-center lg:text-left text-sm text-gray-500">
                    Available across all platforms • One subscription, access
                    everywhere
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mb-6 sm:mb-8 justify-center lg:justify-start">
                  <Link href="/auth?tab=register">
                    <Button
                      size="lg"
                      variant="gradient"
                      className="text-lg px-8 sm:px-10 py-4 sm:py-6 w-full sm:w-auto shadow-lg hover:shadow-xl transition-shadow"
                    >
                      Start Your Free Trial
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>7-day free trial</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>Cancel anytime</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span>No credit card required</span>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative mt-8 lg:mt-0"
              >
                <div className="relative">
                  <div className="w-full h-64 sm:h-80 lg:h-96 bg-gradient-to-br from-purple-100 to-teal-100 rounded-2xl shadow-2xl flex items-center justify-center">
                    <div className="text-center px-4">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-purple-600 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <TeddyBear className="h-10 w-10 sm:h-12 sm:w-12 text-white" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                        Sample Story
                      </h3>
                      <p className="text-gray-600 mb-4 text-sm sm:text-base">
                        "The Brave Little Dragon"
                      </p>
                      <Button
                        variant="gradient"
                        size="sm"
                        className="text-sm cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          const audio = new Audio("/demo-story.mp3");
                          audio.play().catch((error) => {
                            console.error("Error playing audio:", error);
                            // Show error message to user
                            alert("Unable to play audio. Please try again.");
                          });
                        }}
                      >
                        <Play className="mr-2 h-4 w-4" />
                        Play Story
                      </Button>
                    </div>
                  </div>
                  {/* Floating elements */}
                  <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-12 h-12 sm:w-16 sm:h-16 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                    <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                  <div className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 w-8 h-8 sm:w-12 sm:h-12 bg-pink-400 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    <Heart className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Child Profile Hero Ribbon */}
        <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-rose-50 to-pink-50">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Baby className="h-10 w-10 text-white" />
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                Create Personalized Child Profiles - Stories That Truly Feel
                Like Theirs
              </h2>
              <p className="text-lg sm:text-xl text-gray-700 mb-8 max-w-4xl mx-auto leading-relaxed">
                Save your child's details (name, age, gender, language) + upload
                their photo to auto-generate a cartoon character avatar. Every
                story becomes automatically personalized with their info and
                character.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
                <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-base px-4 py-2">
                  <Check className="mr-2 h-4 w-4" />
                  First Profile FREE with subscription
                </Badge>
                <Badge className="bg-gradient-to-r from-rose-500 to-pink-500 text-white text-base px-4 py-2">
                  Additional profiles $1.99 each
                </Badge>
              </div>

              <Link href="/child-profiles">
                <Button
                  size="lg"
                  variant="gradient"
                  className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-shadow"
                  data-testid="button-create-first-profile"
                >
                  Create Your First Profile
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-12 sm:py-20 px-4 bg-white">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                Everything You Need for Magical Storytelling
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
                Our AI-powered platform transforms your photos into cartoon
                characters, creates personalized bedtime stories featuring your
                family, and brings them to life with professional narration and
                beautiful illustrations.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                const gradients = [
                  "from-purple-600 via-pink-600 to-red-600",
                  "from-blue-600 via-cyan-600 to-teal-600",
                  "from-amber-600 via-orange-600 to-red-600",
                  "from-emerald-600 via-green-600 to-lime-600",
                  "from-indigo-600 via-violet-600 to-purple-600",
                  "from-rose-600 via-pink-600 to-fuchsia-600",
                ];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Card
                      className={`h-full hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br ${gradients[index % gradients.length]}`}
                    >
                      <CardHeader className="bg-white/10">
                        <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <CardTitle className="text-xl text-white">
                          {feature.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-base text-white/90">
                          {feature.description}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Story Flow Section - How It Works */}
        <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-purple-50 to-teal-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                From Profile Photo to Magical Story in 3 Easy Steps
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
                Creating personalized stories with your child's custom character
                has never been easier
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 items-start">
              {/* Step 1 */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="relative"
              >
                <Card className="h-full bg-gradient-to-br from-purple-100 to-pink-100 border-2 border-purple-200">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <UserPlus className="h-8 w-8 text-white" />
                    </div>
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                      1
                    </div>
                    <CardTitle className="text-xl text-purple-800">
                      Create Child Profile
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-purple-700 text-center">
                      Save name, age, birthday, gender, and language preferences
                      for personalized storytelling
                    </CardDescription>
                  </CardContent>
                </Card>
                {/* Arrow - hidden on mobile */}
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                  <ArrowRight className="h-8 w-8 text-purple-400" />
                </div>
              </motion.div>

              {/* Step 2 */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <Card className="h-full bg-gradient-to-br from-teal-100 to-cyan-100 border-2 border-teal-200">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Camera className="h-8 w-8 text-white" />
                    </div>
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                      2
                    </div>
                    <CardTitle className="text-xl text-teal-800">
                      Upload Photo & Generate Avatar
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-teal-700 text-center">
                      Our AI creates a cartoon character from their photo - a
                      magical avatar that represents your child
                    </CardDescription>
                  </CardContent>
                </Card>
                {/* Arrow - hidden on mobile */}
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                  <ArrowRight className="h-8 w-8 text-teal-400" />
                </div>
              </motion.div>

              {/* Step 3 */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="relative"
              >
                <Card className="h-full bg-gradient-to-br from-amber-100 to-orange-100 border-2 border-amber-200">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <BookOpen className="h-8 w-8 text-white" />
                    </div>
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                      3
                    </div>
                    <CardTitle className="text-xl text-amber-800">
                      Generate Personalized Stories
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-amber-700 text-center">
                      Character appears in stories with their details
                      auto-filled - truly personalized adventures
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <div className="text-center mt-12">
              <Link href="/auth?tab=register">
                <Button
                  size="lg"
                  variant="gradient"
                  className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-shadow"
                  data-testid="button-get-started-story-flow"
                >
                  Get Started Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials Section - Hidden */}
        {false && (
          <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-purple-50 to-teal-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12 sm:mb-16">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                  Loved by Families Worldwide
                </h2>
                <p className="text-lg sm:text-xl text-gray-600">
                  Join thousands of parents who have discovered the magic of
                  personalized storytelling
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Card className="h-full">
                      <CardContent className="pt-6">
                        <div className="flex items-center mb-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star
                              key={i}
                              className="h-5 w-5 text-yellow-400 fill-current"
                            />
                          ))}
                        </div>
                        <p className="text-gray-700 mb-4 italic">
                          "{testimonial.content}"
                        </p>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {testimonial.name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {testimonial.role}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Verified Profiles Section */}
        <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-amber-50 to-yellow-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Verified Community Members
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
                Join our exclusive community of verified Official Bear Insiders
                who help shape the future of personalized storytelling
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <Card className="h-full bg-gradient-to-br from-amber-100 to-yellow-100 border-2 border-amber-200 hover:shadow-lg transition-shadow duration-300">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <span className="text-2xl">🍯</span>
                    </div>
                    <CardTitle className="text-xl text-amber-800 flex items-center justify-center gap-2">
                      Official Bear Insider
                      <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white border-0">
                        Verified
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-amber-700 text-center">
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-amber-600" />
                          <span>Exclusive honeypot verification shield</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-amber-600" />
                          <span>Early access to new features</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-amber-600" />
                          <span>Direct influence on app development</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-amber-600" />
                          <span>Priority customer support</span>
                        </li>
                      </ul>
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Card className="h-full bg-gradient-to-br from-purple-100 to-pink-100 border-2 border-purple-200 hover:shadow-lg transition-shadow duration-300">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Crown className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl text-purple-800 flex items-center justify-center gap-2">
                      App Founder
                      <Badge className="bg-gradient-to-r from-purple-400 to-pink-500 text-white border-0">
                        Exclusive
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-purple-700 text-center">
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-purple-600" />
                          <span>Founder crown verification badge</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-purple-600" />
                          <span>All premium features included</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-purple-600" />
                          <span>Lifetime access to the platform</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-purple-600" />
                          <span>Special recognition in community</span>
                        </li>
                      </ul>
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Card className="h-full bg-gradient-to-br from-teal-100 to-cyan-100 border-2 border-teal-200 hover:shadow-lg transition-shadow duration-300">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Users className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl text-teal-800 flex items-center justify-center gap-2">
                      Community Member
                      <Badge className="bg-gradient-to-r from-teal-400 to-cyan-500 text-white border-0">
                        Active
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-teal-700 text-center">
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-teal-600" />
                          <span>Share stories with the community</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-teal-600" />
                          <span>Vote on favorite stories</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-teal-600" />
                          <span>Connect with other families</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-teal-600" />
                          <span>Discover new story ideas</span>
                        </li>
                      </ul>
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-600 mb-6">
                Verification badges help you identify trusted community members
                and showcase your status
              </p>
              <Link href="/auth?tab=register">
                <Button
                  size="lg"
                  variant="gradient"
                  className="text-lg px-8 py-4 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <Users className="mr-2 h-5 w-5" />
                  Join Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-purple-50 to-teal-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                Frequently Asked Questions
              </h2>
              <p className="text-lg sm:text-xl text-gray-600">
                Everything you need to know about{" "}
                <span className="text-snuggle-gradient">
                  SnuggleCrafters.com
                </span>
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card>
                    <CardContent className="p-0">
                      <button
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                        onClick={() => toggleFaq(index)}
                      >
                        <span className="font-semibold text-lg">
                          {faq.question}
                        </span>
                        {expandedFaq === index ? (
                          <ChevronUp className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                      <AnimatePresence>
                        {expandedFaq === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-4 text-gray-700">
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-20 px-4 bg-gradient-to-r from-purple-600 to-teal-600">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Create Magical Memories?
            </h2>
            <p className="text-lg sm:text-xl text-purple-100 mb-6 sm:mb-8">
              Join thousands of families who have discovered the joy of
              personalized storytelling
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/auth?tab=register">
                <Button
                  size="lg"
                  variant="gradient"
                  className="text-lg px-6 sm:px-8 py-4 sm:py-6 w-full sm:w-auto text-white"
                >
                  Start Your Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link href="/snugglecrafters-influencer-program">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-6 sm:px-8 py-4 sm:py-6 border-white text-white hover:bg-white hover:text-purple-600 w-full sm:w-auto"
                >
                  <Award className="mr-2 h-5 w-5" />
                  Influencer Program
                </Button>
              </Link>
            </div>
            <p className="text-purple-200 mt-4 sm:mt-6 text-sm">
              No credit card required • 7-day free trial • Cancel anytime
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
